/* eslint-disable react/display-name */
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import Footer from '@Components/Footer';

import './RsTable.scss';

const RsTable = React.forwardRef((props, ref) => {
  const {
    isLoading,
    dataList,
    header = null,
    renderItem = () => null,
    className,
    wrapperClassName,
    containerClassName,
    wrapperRef,
    children,
    showFooter,
    // P2-2 — SDD §9.4: per-instance table label props so callers can override the
    // hardcoded RT-specific label. tableAriaLabelledBy takes precedence over tableAriaLabel.
    tableAriaLabel,
    tableAriaLabelledBy,
  } = props;

  // Build aria label props: labelledby beats label; fall back to the legacy RT string
  // so all existing RsTable usages keep working without any change.
  const tableA11yProps = tableAriaLabelledBy
    ? { 'aria-labelledby': tableAriaLabelledBy }
    : { 'aria-label': tableAriaLabel ?? 'RT Table with the reaction and view details' };
  const noFooterClass = !showFooter ? 'rs-table-without-footer' : undefined;

  return (
    <div ref={wrapperRef} className={cn(wrapperClassName, { 'rs-table-wrapper': showFooter })}>
      <div ref={ref} className={cn('rs-table-container', containerClassName, noFooterClass)}>
        <p id="route-topology-sr" className="sr-only">
          This column shows the Route topology thumbnail, providing a quick overview of the complexity of the found route.
        </p>
        <p id="building-block-sr" className="sr-only">
          This column shows the Building Blocks to target column shows building blocks/starting materials required to synthesize the given
          target compound, and number of steps in each route.
        </p>
        <table
          className={cn(
            'table c-els-table c-els-table--middle c-els-table--vertical-dividers c-els-table--subtle-dividers rs-table c-els-table--highlight-header',
            className
          )}
          {...tableA11yProps}>
          <thead className="c-els-table__head">{header}</thead>
          <tbody className="c-els-table__body">
            {isLoading && (
              <tr>
                <td>
                  <SimpleSpinner />
                </td>
              </tr>
            )}
            {dataList?.map((projectVO, i) => {
              const stableKey = projectVO?.id || projectVO?.key || projectVO?.uuid;
              return <React.Fragment key={stableKey || `project-item-fallback-${i}`}>{renderItem(projectVO, i)}</React.Fragment>;
            })}
          </tbody>
        </table>
        {children}
      </div>
      {showFooter && <Footer className="retro-synthesis-footer" />}
    </div>
  );
});

RsTable.propTypes = {
  isLoading: PropTypes.bool,
  dataList: PropTypes.array,
  header: PropTypes.node,
  showFooter: PropTypes.bool,
  renderItem: PropTypes.func,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  wrapperRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  tableAriaLabel: PropTypes.string,
  tableAriaLabelledBy: PropTypes.string,
};

export default RsTable;
